/**
 * Nationalités ISO 3166-1 alpha-2 → libellé + drapeau (emoji regional indicators).
 * Pas d’API externe : rendu natif Unicode, compatible partout dans l’app.
 */

export interface NationalityEntry {
  code: string;
  label: string;
  flagEmoji: string;
  /** Classe flag-icons si différente du code ISO (ex. gb-sct pour l’Écosse). */
  flagIconCode?: string;
}

export function getFlagEmoji(code: string): string {
  if (!code || code.length !== 2) return '';
  const upper = code.toUpperCase();
  const a = upper.charCodeAt(0) - 0x41 + 0x1f1e6;
  const b = upper.charCodeAt(1) - 0x41 + 0x1f1e6;
  if (a < 0x1f1e6 || a > 0x1f1ff || b < 0x1f1e6 || b > 0x1f1ff) return '';
  return String.fromCodePoint(a, b);
}

const SCOTLAND_FLAG_EMOJI = String.fromCodePoint(
  0x1f3f4,
  0xe0067,
  0xe0062,
  0xe0073,
  0xe0063,
  0xe0074,
  0xe007f
);

const NATIONALITIES_UNSORTED: NationalityEntry[] = [
  { code: 'ZA', label: 'Afrique du Sud', flagEmoji: getFlagEmoji('ZA') },
  { code: 'DE', label: 'Allemagne', flagEmoji: getFlagEmoji('DE') },
  { code: 'AR', label: 'Argentine', flagEmoji: getFlagEmoji('AR') },
  { code: 'AU', label: 'Australie', flagEmoji: getFlagEmoji('AU') },
  { code: 'AT', label: 'Autriche', flagEmoji: getFlagEmoji('AT') },
  { code: 'BE', label: 'Belgique', flagEmoji: getFlagEmoji('BE') },
  { code: 'BO', label: 'Bolivie', flagEmoji: getFlagEmoji('BO') },
  { code: 'BR', label: 'Brésil', flagEmoji: getFlagEmoji('BR') },
  { code: 'BF', label: 'Burkina Faso', flagEmoji: getFlagEmoji('BF') },
  { code: 'CA', label: 'Canada', flagEmoji: getFlagEmoji('CA') },
  { code: 'CN', label: 'Chine', flagEmoji: getFlagEmoji('CN') },
  { code: 'KR', label: 'Corée du Sud', flagEmoji: getFlagEmoji('KR') },
  {
    code: 'EO',
    label: 'Écosse',
    flagEmoji: SCOTLAND_FLAG_EMOJI,
    flagIconCode: 'gb-sct',
  },
  { code: 'ES', label: 'Espagne', flagEmoji: getFlagEmoji('ES') },
  { code: 'US', label: 'États-Unis', flagEmoji: getFlagEmoji('US') },
  { code: 'FI', label: 'Finlande', flagEmoji: getFlagEmoji('FI') },
  { code: 'FR', label: 'France', flagEmoji: getFlagEmoji('FR') },
  { code: 'IN', label: 'Inde', flagEmoji: getFlagEmoji('IN') },
  { code: 'IE', label: 'Irlande', flagEmoji: getFlagEmoji('IE') },
  { code: 'IT', label: 'Italie', flagEmoji: getFlagEmoji('IT') },
  { code: 'JP', label: 'Japon', flagEmoji: getFlagEmoji('JP') },
  { code: 'LV', label: 'Lettonie', flagEmoji: getFlagEmoji('LV') },
  { code: 'LT', label: 'Lituanie', flagEmoji: getFlagEmoji('LT') },
  { code: 'MX', label: 'Mexique', flagEmoji: getFlagEmoji('MX') },
  { code: 'NO', label: 'Norvège', flagEmoji: getFlagEmoji('NO') },
  { code: 'NL', label: 'Pays-Bas', flagEmoji: getFlagEmoji('NL') },
  { code: 'PE', label: 'Pérou', flagEmoji: getFlagEmoji('PE') },
  { code: 'PL', label: 'Pologne', flagEmoji: getFlagEmoji('PL') },
  { code: 'PT', label: 'Portugal', flagEmoji: getFlagEmoji('PT') },
  { code: 'GB', label: 'Royaume-Uni', flagEmoji: getFlagEmoji('GB') },
  { code: 'RU', label: 'Russie', flagEmoji: getFlagEmoji('RU') },
  { code: 'SE', label: 'Suède', flagEmoji: getFlagEmoji('SE') },
  { code: 'CH', label: 'Suisse', flagEmoji: getFlagEmoji('CH') },
];

export const NATIONALITIES: NationalityEntry[] = [...NATIONALITIES_UNSORTED].sort((a, b) =>
  a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' })
);

const LABEL_BY_CODE = new Map(NATIONALITIES.map((n) => [n.code, n.label]));

/** Nom complet du pays pour un code ISO (ex. FR → France). */
export function getNationalityLabel(code: string): string {
  const c = (code ?? '').trim().toUpperCase();
  if (!c) return 'Inconnu';
  return LABEL_BY_CODE.get(c) ?? c;
}

export function getNationalityEntry(code: string): NationalityEntry | undefined {
  const c = (code ?? '').trim().toUpperCase();
  return NATIONALITIES.find((n) => n.code === c);
}

/** Code ISO normalisé (2 lettres) ou null. */
export function normalizeCountryCode(code: string): string | null {
  const c = (code ?? '').trim().toUpperCase();
  if (!c) return null;
  if (NATIONALITIES.some((entry) => entry.code === c)) return c;
  return /^[A-Z]{2}$/.test(c) ? c : null;
}

/** Classe CSS flag-icons (ex. FR → `fi fi-fr`). */
export function getFlagIconClass(code: string): string | null {
  const entry = getNationalityEntry(code);
  if (entry?.flagIconCode) return `fi fi-${entry.flagIconCode}`;
  const c = normalizeCountryCode(code);
  return c ? `fi fi-${c.toLowerCase()}` : null;
}
